/**
 * Theme constants and pure helpers, shared by the server-rendered shell, the
 * no-flash inline script, the React hook and the toggle.
 *
 * Themes are represented as a `data-theme` attribute on <html>:
 *   - "light"  -> the default Subiza look (`:root` in styles.css)
 *   - "lagoon" -> the Deep Lagoon dark theme (`[data-theme="lagoon"]`)
 *
 * This module is intentionally free of browser-only side effects at import
 * time so it can be imported from the root route during SSR.
 */

export const THEMES = ["light", "lagoon"];
export const DEFAULT_THEME = "light";
export const THEME_STORAGE_KEY = "subiza:theme";

/** `theme-color` meta value per theme (keeps mobile browser chrome in sync). */
export const THEME_COLORS = {
  light: "#ffffff",
  lagoon: "#0f1b1e",
};

export function isTheme(value) {
  return value === "light" || value === "lagoon";
}

export function getStoredTheme() {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

/** Stored choice wins; otherwise default to the light theme. */
export function resolveInitialTheme() {
  return getStoredTheme() ?? DEFAULT_THEME;
}

function setMetaThemeColor(theme) {
  if (typeof document === "undefined") return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLORS[theme]);
}

/**
 * Apply a theme to the document. `persist` is false for the initial paint so we
 * never write to localStorage during SSR hydration or on the very first visit.
 */
export function applyTheme(theme, { persist = true } = {}) {
  if (typeof document === "undefined") return;
  const next = isTheme(theme) ? theme : DEFAULT_THEME;
  document.documentElement.setAttribute("data-theme", next);
  setMetaThemeColor(next);
  if (persist) {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* private mode / storage disabled: the theme still applies for the session */
    }
  }
}

/**
 * Runs in <head> before first paint. It must be synchronous, tiny and
 * dependency-free: any flash of the wrong theme is visible to the user.
 */
export const themeInitScript = `(function(){try{
var key=${JSON.stringify(THEME_STORAGE_KEY)};
var stored=localStorage.getItem(key);
var theme=(stored==="light"||stored==="lagoon")?stored:"light";
var el=document.documentElement;
el.setAttribute("data-theme",theme);
var meta=document.querySelector('meta[name="theme-color"]');
if(meta){meta.setAttribute("content",theme==="lagoon"?${JSON.stringify(THEME_COLORS.lagoon)}:${JSON.stringify(THEME_COLORS.light)});}
}catch(e){}})();`;
